import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useId, useState } from "react";
import * as client from "../../../client";
import { standardFormattingKeys } from "../queryKeys";
import styles from "./PolicyFieldsCard.module.css";

/** What kind of value is formatted; a sublimit also carries its classification. */
export type FormattingTarget = Omit<client.StandardFormattingRequest, "text">;

// Waits for a pause in typing before asking for a preview.
const PREVIEW_DEBOUNCE_MS = 300;

const formattingQuery = (request: client.StandardFormattingRequest) => ({
  queryKey: standardFormattingKeys.format(request),
  queryFn: async (): Promise<string> =>
    (await client.standardFormatting({ requestBody: request })).text,
  // The formatted text depends only on the request.
  staleTime: Infinity,
});

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

/**
 * A value being edited with "Apply standard formatting", which starts ticked.
 * `preview` is the formatted text when formatting changes the value, else
 * null. Until a preview for the current text arrives it holds the last one,
 * and `previewIsCurrent` is false. `resolve` returns the text to save: formatted
 * when the box is ticked (rejecting if formatting fails), else the value as
 * typed. Both are trimmed.
 */
export const useFormattedValue = (
  initialValue: string,
  target: FormattingTarget
) => {
  const [value, setValue] = useState(initialValue);
  const [applyFormatting, setApplyFormatting] = useState(true);
  const queryClient = useQueryClient();
  const trimmed = value.trim();
  const debounced = useDebouncedValue(trimmed, PREVIEW_DEBOUNCE_MS);
  const preview = useQuery({
    ...formattingQuery({ ...target, text: debounced }),
    enabled: applyFormatting && debounced !== "",
  });

  // A preview computed for earlier text would read as a formatting change to
  // the current text, so it only counts once the two match.
  const current =
    applyFormatting && trimmed !== "" && debounced === trimmed
      ? preview.data
      : undefined;
  // Kept across renders so the field can hold the last preview's space while
  // the user types.
  const [heldPreview, setHeldPreview] = useState<string | null>(null);
  const shownPreview =
    !applyFormatting || trimmed === ""
      ? null
      : current !== undefined
        ? current !== trimmed
          ? current
          : null
        : heldPreview;
  if (shownPreview !== heldPreview) setHeldPreview(shownPreview);

  const resolve = async (): Promise<string> =>
    applyFormatting && trimmed !== ""
      ? queryClient.fetchQuery(formattingQuery({ ...target, text: trimmed }))
      : trimmed;

  const reset = (): void => {
    setValue(initialValue);
    setApplyFormatting(true);
  };

  return {
    value,
    setValue,
    applyFormatting,
    setApplyFormatting,
    preview: shownPreview,
    previewIsCurrent: current !== undefined,
    resolve,
    reset,
  };
};

export type FormattedValue = ReturnType<typeof useFormattedValue>;

/** The value textarea with its "Apply standard formatting" checkbox, and the
 *  text that will be saved when formatting changes it. */
export const FormattedValueField: React.FC<{
  state: FormattedValue;
  placeholder?: string;
}> = ({ state, placeholder }) => {
  const checkboxId = useId();
  return (
    <>
      <InputTextarea
        className={styles.confirmDialogWidth}
        placeholder={placeholder}
        value={state.value}
        onChange={(e) => state.setValue(e.target.value)}
        autoResize
        rows={3}
      />
      <div className={styles.horizontalContainer}>
        <Checkbox
          inputId={checkboxId}
          checked={state.applyFormatting}
          onChange={(e) => state.setApplyFormatting(Boolean(e.checked))}
        />
        <label htmlFor={checkboxId}>Apply standard formatting</label>
      </div>
      {/* Always rendered, so the dialog keeps its height as the preview comes
          and goes. A stale preview keeps its space but is hidden. */}
      <div className={styles.formattingPreview}>
        {state.preview !== null && (
          <span
            className={state.previewIsCurrent ? undefined : styles.stalePreview}
            aria-hidden={!state.previewIsCurrent}
          >
            Will be saved as: <span>{state.preview}</span>
          </span>
        )}
      </div>
    </>
  );
};
